import { X, ShieldCheck, ExternalLink, Clock, Copy, Check, Loader2,} from "lucide-react";
import { useState } from "react";
import styles from "./ValidarContaModal.module.scss";
import { IKycLink,} from "shared/services/api/dadosBancarios.api";

interface Props {
  onClose: () => void;
  gerarLinkKyc: () => Promise<IKycLink>;
}

export const ValidarContaModal: React.FC<Props> = ({
  onClose,
  gerarLinkKyc,
}) => {

  const [etapa, setEtapa] = useState<1 | 2>(1);

  const [kycLink, setKycLink] =
    useState<IKycLink | null>(null);

  const [loading, setLoading] = useState(false);

  const [erro, setErro] = useState("");

  const [copiado, setCopiado] = useState(false);


  /* =========================================
     CONTINUAR
  ========================================= */

  const handleContinuar = async () => {

    try {

      setLoading(true);

      setErro("");

      const resultado = await gerarLinkKyc();

      setKycLink(resultado);

      setEtapa(2);

    } catch (error) {

      console.error(
        "Erro ao gerar link de validação KYC:",
        error
      );

      setErro(
        "Não foi possível iniciar a validação da conta. Tente novamente."
      );

    } finally {

      setLoading(false);

    }

  };


  /* =========================================
     COPIAR LINK
  ========================================= */

  const handleCopiarLink = async () => {

    if (!kycLink?.url) return;

    try {

      await navigator.clipboard.writeText(
        kycLink.url
      );

      setCopiado(true);

      setTimeout(() => {
        setCopiado(false);
      }, 2000);

    } catch (error) {

      console.error(
        "Erro ao copiar link:",
        error
      );

    }

  };


  /* =========================================
     FORMATAR VALIDADE
  ========================================= */

  const formatarValidade = (
    expiresAt: string
  ) => {

    const data = new Date(expiresAt);

    if (Number.isNaN(data.getTime())) {
      return expiresAt;
    }

    return data.toLocaleString("pt-BR", {
      dateStyle: "short",
      timeStyle: "short",
    });

  };


  return (

    <div
      className={styles.overlay}
      onClick={onClose}
    >

      <div
        className={styles.modal}
        onClick={(e) => e.stopPropagation()}
      >

        {/* =========================================
            FECHAR
        ========================================= */}

        <button
          type="button"
          className={styles.btnFechar}
          onClick={onClose}
          aria-label="Fechar"
        >
          <X size={22} />
        </button>


        {/* ==================================================
            ETAPA 1
        ================================================== */}

        {etapa === 1 && (

          <>

            {/* =========================================
                ÍCONE
            ========================================= */}

            <div className={styles.iconContainer}>

              <ShieldCheck
                size={42}
                strokeWidth={1.8}
              />

            </div>


            {/* =========================================
                CONTEÚDO
            ========================================= */}

            <div className={styles.conteudo}>

              <h2>
                Validar sua conta
              </h2>

              <p>
                Para validar sua conta bancária,
                será necessário realizar uma
                verificação segura.
              </p>

              <p>
                Na próxima etapa, você receberá
                um link para concluir a validação
                da sua identidade.
              </p>

            </div>


            {/* =========================================
                ERRO
            ========================================= */}

            {erro && (

              <div className={styles.erro}>
                {erro}
              </div>

            )}


            {/* =========================================
                AÇÕES
            ========================================= */}

            <div className={styles.acoes}>

              <button
                type="button"
                className={styles.btnCancelar}
                onClick={onClose}
                disabled={loading}
              >
                Cancelar
              </button>

              <button
                type="button"
                className={styles.btnContinuar}
                onClick={handleContinuar}
                disabled={loading}
              >

                {loading ? (

                  <>
                    <Loader2
                      size={18}
                      className={styles.spinner}
                    />

                    Gerando link...
                  </>

                ) : (

                  "Continuar"

                )}

              </button>

            </div>

          </>

        )}


        {/* ==================================================
            ETAPA 2
        ================================================== */}

        {etapa === 2 && kycLink && (

          <>

            {/* =========================================
                ÍCONE
            ========================================= */}

            <div className={styles.iconContainer}>

              <ShieldCheck
                size={42}
                strokeWidth={1.8}
              />

            </div>


            {/* =========================================
                TÍTULO
            ========================================= */}

            <div className={styles.conteudo}>

              <h2>
                Validação da conta
              </h2>

              <p>
                Escaneie o QR Code abaixo para
                continuar a validação da sua conta.
              </p>

            </div>


            {/* =========================================
                QR CODE
            ========================================= */}

            <div className={styles.qrContainer}>

              <img
                src={
                  kycLink.qrCodeBase64.startsWith("data:")
                    ? kycLink.qrCodeBase64
                    : `data:image/png;base64,${kycLink.qrCodeBase64}`
                }
                alt="QR Code para validação da conta"
                className={styles.qrCode}
              />

            </div>


            {/* =========================================
                VALIDADE
            ========================================= */}

            <div className={styles.validade}>

              <Clock size={17} />

              <span>
                Link válido até{" "}
                <strong>
                  {formatarValidade(
                    kycLink.expiresAt
                  )}
                </strong>
              </span>

            </div>


            {/* =========================================
                LINK
            ========================================= */}

            <div className={styles.linkArea}>

              <span>
                Ou abra o link diretamente:
              </span>

              <div className={styles.linkLinha}>

                <span className={styles.link}>
                  {kycLink.url}
                </span>

                <button
                  type="button"
                  className={styles.btnCopiar}
                  onClick={handleCopiarLink}
                  aria-label="Copiar link"
                >

                  {copiado ? (
                    <Check size={18} />
                  ) : (
                    <Copy size={18} />
                  )}

                </button>

              </div>

            </div>


            {/* =========================================
                AÇÕES
            ========================================= */}

            <div className={styles.acoes}>

              <button
                type="button"
                className={styles.btnCancelar}
                onClick={onClose}
              >
                Fechar
              </button>

              <button
                type="button"
                className={styles.btnContinuar}
                onClick={() => {
                  window.open(
                    kycLink.url,
                    "_blank",
                    "noopener,noreferrer"
                  );
                }}
              >

                <ExternalLink size={18} />

                Abrir validação

              </button>

            </div>

          </>

        )}

      </div>

    </div>

  );
};